import React, { useState, useEffect, useCallback } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Divider from '@amzn/meridian/divider';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Icon from '@amzn/meridian/icon';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import FileUploadModal, { FileFieldConfig } from '../components/FileUploadModal';
import ExecutionStatus from '../components/ExecutionStatus';
import { getDSData, triggerCapacityCalculation, uploadCapSettingFiles, getPresignedDownloadUrls, getPresignedUrls } from '../services/api';
import { tomorrow, dayAfterTomorrow } from '../utils/dateUtils';
import { S3_KEYS, resolveS3Key } from '../constants/s3Keys';
import { BATCH_EVENT_TABLE } from '../appConfig';

interface CapacityManagementProps {
    horizon: string;
    program: string;
}

const CAP_SETTING_FILES: FileFieldConfig[] = [
    { key: 'spr', label: '1. SPR' },
    { key: 'volume_forecast', label: '2. D-1 Volume Forecast' },
    { key: 'utr_constraint', label: '3. UTR Constraint' },
    { key: 'flex_incremental', label: '4. Flex Incremental' },
    { key: 'vovi_forecast', label: '5. VoVi Forecast' },
    { key: 'hub_capacity', label: '6. Hub Capacity' },
];

const CAP_DEPLOY_FILES: FileFieldConfig[] = [
    { key: 'cims_file', label: 'CIMS File (.xlsx) - Optional', required: false },
];

const METHODS = ['cap_setting', 'cycle_transfer', 'cap_release', 'cap_deployment'];

const CapacityManagement: React.FC<CapacityManagementProps> = ({ horizon, program }) => {
    const [error, setError] = useState<string | null>(null);
    const [selectedRound, setSelectedRound] = useState<string>('1');
    const [isCapSettingModalOpen, setIsCapSettingModalOpen] = useState(false);
    const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
    const [stages, setStages] = useState<Record<string, { status: string; timestamp?: string; user?: string }>>({});
    const [polling, setPolling] = useState(false);
    const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    const downloadButtonRef = React.useRef<HTMLElement>(null);
    const initialLoadDone = React.useRef(false);
    const [isCapDeployModalOpen, setIsCapDeployModalOpen] = useState(false);
    const [cmIds, setCmIds] = useState<string[]>([]);

    const ofdDate = horizon === 'D-2' ? dayAfterTomorrow() : tomorrow();

    const refreshBatchStatus = useCallback(async (batchId: string) => {
        try {
            const result = await getDSData(BATCH_EVENT_TABLE, {
                filters: [{ key: 'batch_id', operator: 'eq', value: batchId }],
            });
            const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            if (items.length > 0) {
                const latest = items[0];
                const loadedStages: Record<string, { status: string; timestamp?: string; user?: string }> = {};
                let hasInProgress = false;
                for (const method of METHODS) {
                    const status = latest[`${method}_status`];
                    if (status) {
                        loadedStages[method] = {
                            status,
                            timestamp: latest[`${method}_completed_at`] || latest[`${method}_updated_at`] || '',
                            user: latest[`${method}_user`] || '',
                        };
                        if (status === 'IN_PROGRESS') hasInProgress = true;
                    }
                }
                setStages(loadedStages);
                // Extract CM IDs from cm_id field (pipe-separated, e.g. "67819|67820|67821")
                if (latest.cm_id) {
                    const ids = latest.cm_id.split('|').map((id: string) => id.trim()).filter(Boolean);
                    setCmIds(ids);
                } else {
                    setCmIds([]);
                }
                if (!hasInProgress) setPolling(false);
            }
        } catch (err) {
            // silent
        }
    }, []);

    const loadBatchForRound = useCallback(async (round: string) => {
        try {
            const result = await getDSData(BATCH_EVENT_TABLE, {
                filters: [
                    { key: 'horizon', operator: 'eq', value: horizon },
                    { key: 'process', operator: 'eq', value: program },
                    { key: 'ofd_date', operator: 'eq', value: ofdDate },
                    { key: 'round', operator: 'eq', value: `R${round}` },
                ],
            });
            const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            const sorted = items.sort((a: any, b: any) =>
                new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
            );
            if (sorted.length > 0) {
                const latest = sorted[0];
                console.log('Latest batch for round R' + round + ':', latest.batch_id);
                setCurrentBatchId(latest.batch_id);
                await refreshBatchStatus(latest.batch_id);
            } else {
                setCurrentBatchId(null);
                setStages({});
            }
        } catch (err) {
            console.log('loadBatchForRound error:', err);
        }
    }, [horizon, program, ofdDate, refreshBatchStatus]);

    useEffect(() => {
        const loadLatestBatch = async () => {
            try {
                const result = await getDSData(BATCH_EVENT_TABLE, {
                    filters: [
                        { key: 'horizon', operator: 'eq', value: horizon },
                        { key: 'process', operator: 'eq', value: program },
                        { key: 'ofd_date', operator: 'eq', value: ofdDate },
                    ],
                });
                const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
                console.log('Batch items count:', items.length, 'horizon:', horizon, 'program:', program, 'ofdDate:', ofdDate);
                const sorted = items.sort((a: any, b: any) =>
                    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
                );
                if (sorted.length > 0) {
                    const latest = sorted[0];
                    console.log('Latest batch:', latest.batch_id, 'round:', latest.round);
                    setCurrentBatchId(latest.batch_id);
                    if (latest.round) {
                        setSelectedRound(latest.round.replace('R', ''));
                    }
                    await refreshBatchStatus(latest.batch_id);
                }
            } catch (err) {
                console.log('loadLatestBatch error:', err);
            } finally {
                initialLoadDone.current = true;
            }
        };
        loadLatestBatch();
    }, [horizon, program, ofdDate, refreshBatchStatus]);

    // When user changes the round dropdown, load the latest batch for that round
    useEffect(() => {
        if (!initialLoadDone.current) return;
        loadBatchForRound(selectedRound);
    }, [selectedRound, loadBatchForRound]);

    useEffect(() => {
        if (!polling || !currentBatchId) return;
        const interval = setInterval(() => refreshBatchStatus(currentBatchId), 5000);
        return () => clearInterval(interval);
    }, [polling, currentBatchId, refreshBatchStatus]);

    const handleCapSettingSubmit = async (files: Record<string, File>) => {
        const batchId = crypto.randomUUID();
        const round = `R${selectedRound}`;
        setCurrentBatchId(batchId);
        setStages({ cap_setting: { status: 'IN_PROGRESS' } });
        setPolling(true);

        try {
            await uploadCapSettingFiles(files, batchId, round, horizon);
            setIsCapSettingModalOpen(false);
            triggerCapacityCalculation({
                method: 'cap_setting',
                batch_id: batchId,
                horizon,
                round,
            }).catch((err) => {
                if (!err.message?.includes('504')) {
                    setError(err.message || 'Failed to trigger cap setting');
                    setPolling(false);
                }
            });
        } catch (err: any) {
            setIsCapSettingModalOpen(false);
            setError(err.message || 'File upload failed');
        }
    };

    const handleCycleTransfer = () => {
        if (!currentBatchId) return;
        setStages(prev => ({ ...prev, cycle_transfer: { status: 'IN_PROGRESS' } }));
        setPolling(true);
        triggerCapacityCalculation({
            method: 'cycle_transfer',
            batch_id: currentBatchId,
            horizon,
            round: `R${selectedRound}`,
        }).catch((err) => {
            if (!err.message?.includes('504')) {
                setError(err.message || 'Failed to trigger cycle transfer');
                setPolling(false);
            }
        });
    };

    const handleCapRelease = () => {
        if (!currentBatchId) return;
        setStages(prev => ({ ...prev, cap_release: { status: 'IN_PROGRESS' } }));
        setPolling(true);
        triggerCapacityCalculation({
            method: 'cap_release',
            batch_id: currentBatchId,
            horizon,
            round: `R${selectedRound}`,
        }).catch((err) => {
            if (!err.message?.includes('504')) {
                setError(err.message || 'Failed to trigger cap release');
                setPolling(false);
            }
        });
    };

    const handleCapDeploySubmit = async (files: Record<string, File>) => {
        if (!currentBatchId) return;
        setStages(prev => ({ ...prev, cap_deployment: { status: 'IN_PROGRESS' } }));
        setPolling(true);

        try {
            const round = `R${selectedRound}`;

            // Upload CIMS file only if provided
            if (files.cims_file) {
                const templates = S3_KEYS[program]?.[horizon]?.cap_deployment?.inputs;
                if (templates?.cims_file) {
                    const s3Key = resolveS3Key(templates.cims_file, { batchId: currentBatchId, round, horizon });
                    const { urls } = await getPresignedUrls([s3Key]);
                    if (urls[s3Key]) {
                        await fetch(urls[s3Key], { method: 'PUT', body: files.cims_file });
                    }
                }
            }

            setIsCapDeployModalOpen(false);
            triggerCapacityCalculation({
                method: 'cap_deployment',
                batch_id: currentBatchId,
                horizon,
                round,
            }).catch((err) => {
                if (!err.message?.includes('504')) {
                    setError(err.message || 'Failed to trigger cap deployment');
                    setPolling(false);
                }
            });
        } catch (err: any) {
            setIsCapDeployModalOpen(false);
            setError(err.message || 'File upload failed');
        }
    };

    const handleStageClick = (value: string) => {
        switch (value) {
            case 'cap_setting': setIsCapSettingModalOpen(true); break;
            case 'cycle_transfer': handleCycleTransfer(); break;
            case 'cap_release': handleCapRelease(); break;
            case 'cap_deployment': setIsCapDeployModalOpen(true); break;
        }
    };

    const handleDownload = async (value: string) => {
        try {
            const [stage, type, key] = value.split('::');
            const horizonKeys = S3_KEYS[program]?.[horizon];
            if (!horizonKeys) return;
            const stageKeys = horizonKeys[stage as keyof typeof horizonKeys];
            if (!stageKeys) return;

            const template = type === 'inputs' ? stageKeys.inputs[key] : stageKeys.outputs[key];
            if (!template) return;

            const batchId = currentBatchId || 'latest';
            const round = `R${selectedRound}`;
            const s3Key = resolveS3Key(template, { batchId, round });

            const { urls } = await getPresignedDownloadUrls([s3Key]);
            if (urls[s3Key]) window.open(urls[s3Key], '_blank');
        } catch (err: any) {
            setError(err.message || 'Download failed');
        }
    };

    return (
        <Column spacing="500">
            <Heading level={3} type="h300">{horizon} {program} Capacity Management</Heading>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

            <Row width="100%" widths="fill" wrap="none" alignmentHorizontal="justify" alignmentVertical="bottom">
                <Input value={ofdDate} label="OFD Date" disabled />
                <Select value={selectedRound} onChange={setSelectedRound} label="Round">
                    <SelectOption value="1" label="Round 1" />
                    <SelectOption value="2" label="Round 2" />
                    <SelectOption value="3" label="Round 3" />
                </Select>
                 <ButtonGroup value={undefined} onChange={handleStageClick} type="fill">
                    <ButtonOption value="cap_setting" disabled={false}>
                        Cap Setting
                    </ButtonOption>
                    <ButtonOption value="cycle_transfer" disabled={stages.cap_setting?.status !== 'COMPLETED'}>
                        Cycle Transfer
                    </ButtonOption>
                    <ButtonOption value="cap_release" disabled={stages.cycle_transfer?.status !== 'COMPLETED'}>
                        Cap Release
                    </ButtonOption>
                    <ButtonOption value="cap_deployment" disabled={stages.cap_release?.status !== 'COMPLETED'}>
                        Cap Deployment
                    </ButtonOption>
                </ButtonGroup>
                <Button
                    ref={downloadButtonRef}
                    onClick={() => setDownloadMenuOpen(!downloadMenuOpen)}
                    type="secondary"
                >
                    <Icon tokens={downloadLargeTokens}>Download</Icon>
                    Download
                    <Icon tokens={caretDownTokens} />
                </Button>
                    {downloadButtonRef.current && (
                        <Menu
                            anchorNode={downloadButtonRef.current}
                            open={downloadMenuOpen}
                            onClose={() => setDownloadMenuOpen(false)}
                            position='bottom'
                        >
                            <Text type="b100" color="secondary" alignment="center">Cap Setting</Text>
                            <MenuItem onClick={() => { handleDownload('cap_setting::inputs::spr'); setDownloadMenuOpen(false); }}>📥 SPR</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::inputs::volume_forecast'); setDownloadMenuOpen(false); }}>📥 Volume Forecast</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::inputs::utr_constraint'); setDownloadMenuOpen(false); }}>📥 UTR Constraint</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::inputs::flex_incremental'); setDownloadMenuOpen(false); }}>📥 Flex Incremental</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::inputs::vovi_forecast'); setDownloadMenuOpen(false); }}>📥 VoVi Forecast</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::outputs::otr_capacity'); setDownloadMenuOpen(false); }}>📤 OTR Capacity</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::outputs::otr_capacity_log'); setDownloadMenuOpen(false); }}>📤 OTR Capacity Log</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::outputs::utr_capacity'); setDownloadMenuOpen(false); }}>📤 UTR Capacity</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_setting::outputs::utr_capacity_log'); setDownloadMenuOpen(false); }}>📤 UTR Capacity Log</MenuItem>
                            <Divider />
                            <Text type="b100" color="secondary" alignment="center">Cycle Transfer</Text>
                            <MenuItem onClick={() => { handleDownload('cycle_transfer::outputs::cycle_transfer'); setDownloadMenuOpen(false); }}>📤 Cycle Transfer</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cycle_transfer::outputs::cycle_transfer_log'); setDownloadMenuOpen(false); }}>📤 Cycle Transfer Log</MenuItem>
                            <Divider />
                            <Text type="b100" color="secondary" alignment="center">Cap Release</Text>
                            <MenuItem onClick={() => { handleDownload('cap_release::outputs::cap_release_cims'); setDownloadMenuOpen(false); }}>📤 Cap Release CIMS</MenuItem>
                            <MenuItem onClick={() => { handleDownload('cap_release::outputs::cap_release_log'); setDownloadMenuOpen(false); }}>📤 Cap Release Log</MenuItem>
                            {stages.cap_deployment?.status === 'COMPLETED' && cmIds.length > 0 && (
                                <>
                                    <Divider />
                                    <Text type="b100" color="secondary" alignment="center">Cap Deployment</Text>
                                    {cmIds.map((id) => (
                                        <MenuItem key={id} onClick={() => { window.open(`https://starkapps-amzl.noc.amazon.dev/cws/change-management/workflow?id=CM-${id}&country=BR`, '_blank'); setDownloadMenuOpen(false); }}>
                                            {`🔗 CM-${id}`}
                                        </MenuItem>
                                    ))}
                                </>
                            )}
                        </Menu>
                    )}
            </Row>

            <ExecutionStatus batchId={currentBatchId} stages={stages} />

            <FileUploadModal
                open={isCapSettingModalOpen}
                onClose={() => setIsCapSettingModalOpen(false)}
                onSubmit={handleCapSettingSubmit}
                title={`Cap Setting — Upload Files (${horizon} R${selectedRound})`}
                fields={CAP_SETTING_FILES}
                loadingLabel="Uploading..."
            />

            <FileUploadModal
                open={isCapDeployModalOpen}
                onClose={() => setIsCapDeployModalOpen(false)}
                onSubmit={handleCapDeploySubmit}
                title={`Cap Deployment — Upload CIMS (${horizon} R${selectedRound})`}
                fields={CAP_DEPLOY_FILES}
                loadingLabel="Uploading..."
            />
        </Column>
    );
};

export default CapacityManagement;
